import React from 'react';
import { Field, Checkbox, NumericInput, TextOutput, RadioGroup } from '../../../../Core';
import { ListFieldSection, FieldSet } from '../../../../Layout';
import { withInjectedProps, NamePrefixModifier } from '../../../../Core/InjectedPropsContext';
import { useSelector } from 'react-redux';
import { WorksheetEquipmentDropdown } from '../../../Core';
import deepEquals from '../../../../../Lib/Object/deepEquals';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { pipe } from 'Lib/Hooks/Selectors';
import { WorksheetBalanceFieldSet } from '../../../Core/WorksheetEquipment';
import { hasValue } from 'Lib/Any';

let AsCompactionEquipmentSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const mouldEquipment = useSelector(createWorksheetSelector!(`Equipment.Mould`), deepEquals);
  const mouldMass = useSelector(createWorksheetSelector!(`Equipment.MouldMass`), deepEquals);
  const mouldVolume = useSelector(createWorksheetSelector!(`Equipment.MouldVolume`), deepEquals);
  const mouldCalibrationMismatch = useSelector(createWorksheetSelector!(`Equipment.MouldCalibrationMismatch`));

  const hammerEquipment = useSelector(createWorksheetSelector!(`Equipment.Hammer`), deepEquals);
  const hammerOperation = useSelector(createWorksheetSelector!(`Equipment.HammerOperation`));
  const hammerFaceType = useSelector(createWorksheetSelector!(`Equipment.HammerFaceType`));
  const hammerFaceNominalDiameter = useSelector(createWorksheetSelector!(`Equipment.HammerFaceDiameter`), deepEquals);
  const hammerFaceNominalRadius = useSelector(createWorksheetSelector!(`Equipment.HammerFaceRadius`), deepEquals);
  const hammerFaceNominalUnits = useSelector(createWorksheetSelector!(`Equipment.HammerFaceNominalUnits`));
  const hammerImportMismatch = useSelector(createWorksheetSelector!(`Equipment.HammerImportMismatch`));

  const hammerFaceTypeOptions = [
    { label: 'Circular', value: 'circular' },
    { label: 'Sector (Pie)', value: 'sector' },
  ];

  const hammerOperationOptions = ['Automatic', 'Manual'].map((e) => ({ label: e, value: e }));

  /**
   * Determines whether a test portion has any density mould measurements that would be
   * overwritten when checking the 'use for all test portions' checkbox.
   */
  const hasNonMatchingMould = (portion: any) =>
    (hasValue(portion.Mould?.Id) && portion.Mould?.Id !== mouldEquipment?.Id) ||
    (hasValue(portion.MouldMass?.value) && portion.MouldMass?.value !== mouldMass?.value) ||
    (hasValue(portion.MouldVolume?.value) && portion.MouldVolume?.value !== mouldVolume?.value);

  const testPortionHasNonMatchingMould = useSelector(
    pipe(createWorksheetSelector!(`TestPortions`), (p: Array<any>) => p.some(hasNonMatchingMould))
  );

  return (
    <ListFieldSection heading="Equipment">
      <NamePrefixModifier appendNamePrefix="Equipment">
        <WorksheetBalanceFieldSet name="CompactionBalance" label="Balance" />
        <Field input={<WorksheetEquipmentDropdown />} equipmentType="straight-edge" name="StraightEdge" />
        <FieldSet>
          <Field
            input={<WorksheetEquipmentDropdown />}
            equipmentType="hammer"
            name="Hammer"
            importedAdditionalData={{
              Operation: hammerOperation,
              FaceType: hammerFaceType,
              FaceDiameter: hammerFaceNominalDiameter,
              FaceRadius: hammerFaceNominalRadius,
              NominalUnits: hammerFaceNominalUnits,
              Mismatch: hammerImportMismatch,
            }}
            importModel={{
              Id: hammerEquipment?.Id,
            }}
          />
          {hasValue(hammerEquipment?.Operation) ? (
            <Field
              input={<TextOutput />}
              name={`HammerOperation`}
              value={hammerOperationOptions.find((o) => o.value === hammerOperation)?.label}
              label="Operation"
            />
          ) : (
            <Field input={<RadioGroup />} name={`HammerOperation`} options={hammerOperationOptions} label="Operation" />
          )}
          {hasValue(hammerEquipment?.FaceType) ? (
            <Field
              input={<TextOutput />}
              name={`HammerFaceType`}
              value={hammerFaceTypeOptions.find((o) => o.value === hammerFaceType)?.label}
              label="Face Type"
            />
          ) : (
            <Field input={<RadioGroup />} name="HammerFaceType" options={hammerFaceTypeOptions} label="Face Type" />
          )}
          {hammerFaceType === 'circular' && (
            <Field
              input={hasValue(hammerEquipment?.FaceDiameter) ? <TextOutput /> : <NumericInput />}
              name="HammerFaceDiameter"
              label="Face Nominal Diameter"
            />
          )}
          {hammerFaceType === 'sector' && (
            <Field
              input={hasValue(hammerEquipment?.FaceRadius) ? <TextOutput /> : <NumericInput />}
              name="HammerFaceRadius"
              label="Face Nominal Radius"
            />
          )}
        </FieldSet>
        <FieldSet>
          <Field
            input={<WorksheetEquipmentDropdown />}
            equipmentType="density-mould"
            name={`Mould`}
            importedCalibration={{
              Mass: mouldMass,
              Volume: mouldVolume,
              Mismatch: mouldCalibrationMismatch,
            }}
            importModel={{
              Id: mouldEquipment?.Id,
            }}
          />
          <Field
            input={<Checkbox />}
            name={`UseMouldForAllTestPortions`}
            confirm={({ value }) => value === true && testPortionHasNonMatchingMould}
            confirmText="The selected mould will be used for all test portions, overwriting any existing data."
            confirmButtonText="Use mould"
          />
          <Field
            input={hasValue(mouldEquipment?.Mass) ? <TextOutput /> : <NumericInput />}
            name="MouldMass"
            label="Mass"
          />
          <Field
            input={hasValue(mouldEquipment?.Volume) ? <TextOutput /> : <NumericInput />}
            name="MouldVolume"
            label="Volume"
          />
        </FieldSet>
      </NamePrefixModifier>
    </ListFieldSection>
  );
};

AsCompactionEquipmentSection = withInjectedProps(AsCompactionEquipmentSection);

export { AsCompactionEquipmentSection };
export default AsCompactionEquipmentSection;
